import { FILTER_GROUPS } from "config/filters";

/**
 * Storage.com landing-page slug -> gateway query shape (selfstorage_api_gateway
 * SearchClient._cleanQueryParams). Amenity/size/locker pages send { amenity: <token> };
 * vehicle pages send { type: "vehicle", vehicle: x } - a different, non-interchangeable code path.
 *
 * ⚠️ An unmapped/unrecognized token is silently ignored by the gateway (unfiltered city
 * results, no error), so keep tokens in sync with the gateway's amenityMap/sizeMap, and keep
 * this map's keys in sync with server/render.js's landing-page slug allowlist (which imports
 * LANDING_PAGE_QUERY directly).
 */
const LANDING_PAGE_QUERY = {
	// --- Amenity pages ---
	"climate-control-storage-units": { amenity: "climate-controlled" },
	"twenty-four-hour-access-storage-units": { amenity: "24-hour-access" },
	"drive-up-storage-units": { amenity: "drive-up-self-storage" },
	"storage-units-with-electricity": { amenity: "electricity" },
	"storage-units-with-truck-rental": { amenity: "free-truck-rental" },
	"wine-storage-units": { amenity: "wine-storage" },

	// --- Size pages  ---
	"mini-storage": { amenity: "mini-self-storage" },
	"locker-storage": { amenity: "self-storage-lockers" },
	"5x5-storage": { amenity: "5x5-storage" },
	"5x10-storage": { amenity: "5x10-storage" },
	"5x15-storage": { amenity: "5x15-storage" },
	"10x10-storage": { amenity: "10x10-storage" },
	"10x15-storage": { amenity: "10x15-storage" },
	"10x20-storage": { amenity: "10x20-storage" },
	"10x25-storage": { amenity: "10x25-storage" },
	"10x30-storage": { amenity: "10x30-storage" },

	// --- Vehicle pages ---
	//
	// 🔴 DO NOT add `sort` here. "distance" is a user-selectable sort option (SORT_TYPES in
	// config/filters.js), so sending it makes the gateway's isUserAppliedSort() read the page as a
	// user-sorted permutation of the CLP — which emits noindex,nofollow and suppresses the
	// canonical. That deindexed every VLP twice; the second time it was ~853 pages across 285
	// cities. With no sort here, originalSort stays undefined, isDefaultSort() returns true, and
	// the page keeps its canonical.
	//
	// The previous comment claimed the gateway *required* sort:"distance", because it appends
	// "_vehicle" to whatever sort arrives and the site default algo isn't a real algorithm once
	// suffixed. Verified 2026-08-19 that this is not so: search service returns 200 for
	// distance_vehicle, yielddistancelowltveconmir100forced_vehicle, CronutNVF100DonutsLowLTV_vehicle
	// and even a deliberately bogus algo — all with identical total_listings and identical
	// ordering. The sort is ignored for vehicle searches, so no gateway change is needed.
	//
	// vehicleAccommodation IS required: it is what auto-checks the matching filter-panel option.
	"boat-storage-units": {
		type: "vehicle",
		vehicle: "boat",
		vehicleAccommodation: "boat-storage"
	},
	"car-storage-units": {
		type: "vehicle",
		vehicle: "car",
		vehicleAccommodation: "car-storage"
	},
	"rv-storage-units": {
		type: "vehicle",
		vehicle: "rv",
		vehicleAccommodation: "rv-storage"
	}
};

export { LANDING_PAGE_QUERY };

// subPageType (the gateway token, see below) -> the filter option's key
// (config/filters.js), so an ALP's own defining filter can be matched against
// the checkbox options in Redux - mirrors nearMeKeywords.js's
// amenitySlugToFilterValue/getNMPBaseFilters for NMPs.
const ALP_BASE_FILTER_BY_SUBPAGE_TYPE = {
	"climate-controlled": {
		name: FILTER_GROUPS.AMENITIES,
		value: "climateControlled"
	},
	"24-hour-access": {
		name: FILTER_GROUPS.AMENITIES,
		value: "twentyFourHourAccess"
	},
	"drive-up-self-storage": {
		name: FILTER_GROUPS.AMENITIES,
		value: "driveUp"
	},
	electricity: { name: FILTER_GROUPS.AMENITIES, value: "power" },
	"free-truck-rental": {
		name: FILTER_GROUPS.AMENITIES,
		value: "freeTruckRental"
	},
	"wine-storage": { name: FILTER_GROUPS.AMENITIES, value: "onlyWine" },
	// The gateway can echo "mini" as the overlapping numeric bucket below
	// instead of "mini" itself - see search.js's getSearchPage.fulfilled.
	"mini-self-storage": {
		name: FILTER_GROUPS.UNIT_SIZE,
		value: "mini",
		collidesWith: "0-25"
	},
	"self-storage-lockers": {
		name: FILTER_GROUPS.UNIT_SIZE,
		value: "onlyLocker"
	},
	"5x5-storage": { name: FILTER_GROUPS.UNIT_SIZE, value: "0-25" },
	"5x10-storage": { name: FILTER_GROUPS.UNIT_SIZE, value: "26-60" },
	"5x15-storage": { name: FILTER_GROUPS.UNIT_SIZE, value: "61-75" },
	"10x10-storage": { name: FILTER_GROUPS.UNIT_SIZE, value: "76-100" },
	"10x15-storage": { name: FILTER_GROUPS.UNIT_SIZE, value: "101-150" },
	"10x20-storage": { name: FILTER_GROUPS.UNIT_SIZE, value: "151-200" },
	"10x25-storage": { name: FILTER_GROUPS.UNIT_SIZE, value: "201-250" },
	"10x30-storage": { name: FILTER_GROUPS.UNIT_SIZE, value: "251-300" },
	boat: { name: FILTER_GROUPS.VEHICLE_STORAGE, value: "boat-storage" },
	car: { name: FILTER_GROUPS.VEHICLE_STORAGE, value: "car-storage" },
	rv: { name: FILTER_GROUPS.VEHICLE_STORAGE, value: "rv-storage" }
};

// Returns the ALP's own defining filter as [{ name, value }], or [] for the
// plain city page/an unmapped subPageType. Used by applySearchClient to detect
// when the user has unchecked the filter that defines the page, so Apply can
// send them to the city's landing page instead - same idea as
// getNMPBaseFilters, applied to amenityLandingPage.
export function getALPBaseFilters(subPageType) {
	const base = ALP_BASE_FILTER_BY_SUBPAGE_TYPE[subPageType];
	return base ? [base] : [];
}

// subPageType -> curated breadcrumb label, mirroring nearMeKeywords.js's
// breadcrumbLabel. Kept in sync with storageMap.js's labels where one exists.
const ALP_BREADCRUMB_LABEL_BY_SUBPAGE_TYPE = {
	"climate-controlled": "Climate Controlled Storage Units",
	"24-hour-access": "24 Hour Storage Units",
	"drive-up-self-storage": "Drive Up Storage Units",
	electricity: "Storage Units with Electricity",
	"free-truck-rental": "Free Truck Rental Units",
	"wine-storage": "Wine Storage Units",
	"mini-self-storage": "Mini Storage Units",
	"self-storage-lockers": "Storage Lockers",
	"5x5-storage": "5x5 Storage Units",
	"5x10-storage": "5x10 Storage Units",
	"5x15-storage": "5x15 Storage Units",
	"10x10-storage": "10x10 Storage Units",
	"10x15-storage": "10x15 Storage Units",
	"10x20-storage": "10x20 Storage Units",
	"10x25-storage": "10x25 Storage Units",
	"10x30-storage": "10x30 Storage Units",
	boat: "Boat Storage Units",
	car: "Car Storage Units",
	rv: "RV Storage Units"
};

// Returns the static breadcrumb label for an ALP subPageType, or null if unknown.
export function getALPBreadcrumbLabel(subPageType) {
	return ALP_BREADCRUMB_LABEL_BY_SUBPAGE_TYPE[subPageType] || null;
}

export default function buildLandingQuery(match, queryParams, landingType) {
	const { city, state } = match.params;
	const location = `${city}, ${state}`;
	const pageType =
		landingType === "city" ? "cityLandingPage" : "amenityLandingPage"; // old LP types are needed for SEO service
	let query = {
		...queryParams,
		city,
		state,
		location,
		pageType
	};

	if (landingType === "amenity") {
		const { amenity: slug } = match.params;
		const mapped = LANDING_PAGE_QUERY[slug];

		// An unmapped slug would otherwise render a page identical to the plain
		// city page - a silent generic result rather than a visible failure. Since
		// routes.js is generated from this map, reaching here with an unknown slug
		// means routes.js and this map have drifted.
		if (!mapped) {
			return query;
		}

		// subPageType is what the gateway keys its content-template lookup on
		// (SiteContentClient.getContentTemplateId), so it tracks the gateway token
		// (or vehicle name) rather than the URL slug.
		query = {
			...query,
			...mapped,
			subPageType: mapped.amenity || mapped.vehicle
		};
	}

	return query;
}
